import { Navigate, Outlet } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useLocalStorage } from 'usehooks-ts';

import UserService from '../services/UserService';
import { UserRole } from '../services/models/user';

const AuthGuard = () => {
  const [token] = useLocalStorage<string | null>('token', null, {
    serializer: (value) => (value ?? ''),
    deserializer: (value) => value,
  });

  const { data: user, isLoading, isError } = useQuery({
    queryKey: ['user'],
    queryFn: () => UserService.getUser(),
    enabled: !!token,
    select: (response) => response?.data,
  });

  if (!token || isError) {
    return <Navigate to="/login" />;
  }

  if (isLoading) {
    return null;
  }

  // Teammates keep the USER role, so gating on the role alone would lock them
  // out entirely. A fresh OWNER with zero locations must still reach the
  // create-first-site flow, hence the role check alongside the access block.
  if (user) {
    const access = user.access;
    const allowed = access
      ? access.kind === 'admin'
        || [UserRole.ADMIN, UserRole.OWNER].includes(user.role)
        || access.locations.length > 0
      // Older API without the access block: fall back to the legacy rule.
      : [UserRole.ADMIN, UserRole.OWNER].includes(user.role);

    if (!allowed) {
      return <Navigate to="/logout" />;
    }
  }

  return <Outlet />;
};

export default AuthGuard;
